"use client";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import Image from "next/image";

interface ResponsiveConfig {
    desktop?: number;
    tablet?: number;
    mobile?: number;
}

type SliderVariant =
    | "default"
    | "boxed"
    | "boxed-icon"
    | "color-section"
    | "modal-dots"
    | "arrow-top"
    | "trending";

interface SliderProps {
    children: React.ReactNode;
    itemsToShow?: number;
    showArrows?: boolean;
    gap?: number;
    responsive?: ResponsiveConfig;
    showDots?: boolean;
    className?: string;
    activeIndex?: number;
    onSlideChange?: (index: number) => void;
    infinite?: boolean;
    autoSlide?: boolean;
    autoSlideInterval?: number;
    prevBtnTrackingClass?: string;
    nextBtnTrackingClass?: string;
    variant?: SliderVariant;
}

const SWIPE_THRESHOLD = 50;

// Shared arrow behaviour, independent of visual variant.
const ARROW_BASE =
    "absolute z-9 flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none";

const VARIANT_ARROW: Record<SliderVariant, string> = {
    default:
        "hidden md:flex w-10 h-10 rounded-[20px] shadow-[0_3px_9px_rgba(51,51,51,0.2)] bg-[#F5F5F5] bottom-[-50px]",
    boxed:
        "flex w-[26px] h-[37px] max-[767px]:w-[35px] max-[767px]:h-[60px] max-[767px]:opacity-95 backdrop-blur-[6px] border border-[#ffffff0f] rounded-none bg-black/[0.58] top-1/2 -translate-y-1/2",
    "boxed-icon":
        "flex w-[26px] h-[37px] max-[767px]:w-[35px] max-[767px]:h-[60px] max-[767px]:opacity-95 backdrop-blur-[6px] border border-[#ffffff0f] rounded-none bg-black/[0.58] top-1/2 -translate-y-1/2",
    "color-section":
        "flex w-10 h-10 rounded-full shadow-[0_3px_9px_rgba(51,51,51,0.2)] bg-[#F5F5F5] backdrop-blur-[6px] border border-[#ffffff0f] top-1/2 -translate-y-1/2",
    "modal-dots":
        "flex z-20 w-[26px] h-[37px] max-[767px]:w-[25px] max-[767px]:h-10 rounded-none bg-black/60 text-white outline-none top-1/2 -translate-y-1/2",
    "arrow-top":
        "hidden md:flex w-10 h-10 rounded-[20px] shadow-[0_3px_9px_rgba(51,51,51,0.2)] bg-[#F5F5F5] top-[-40px]",
    trending:
        "hidden md:flex w-10 h-10 rounded-[20px] shadow-[0_3px_9px_rgba(51,51,51,0.2)] backdrop-blur-[10px] border border-[#009689]/47 bg-[#009689]/44 top-[-40px] -translate-y-1/2",
};

const VARIANT_LEFT: Record<SliderVariant, string> = {
    default: "right-[70px]",
    boxed: "left-0",
    "boxed-icon": "left-0",
    "color-section": "left-[10px]",
    "modal-dots": "left-0",
    "arrow-top": "right-[48px]",
    trending: "right-[48px]",
};

const VARIANT_RIGHT: Record<SliderVariant, string> = {
    default: "right-[10px]",
    boxed: "right-0",
    "boxed-icon": "right-0",
    "color-section": "right-[10px]",
    "modal-dots": "right-0",
    "arrow-top": "right-0",
    trending: "right-0",
};

// Variants whose arrow chrome hides the built-in white icon tint.
const NATURAL_ICON_VARIANTS: SliderVariant[] = ["boxed-icon", "modal-dots"];
const ICON_SIZE_OVERRIDE: Partial<Record<SliderVariant, string>> = {
    "boxed-icon": "w-4 h-4",
};

const VARIANT_TRACK_SPACING: Record<SliderVariant, string> = {
    default: "mx-[-18px] px-1.5",
    boxed: "mx-0 px-0",
    "boxed-icon": "mx-0 px-0",
    "color-section": "mx-[-18px] px-1.5",
    "modal-dots": "mx-[-18px] px-1.5",
    "arrow-top": "mx-[-18px] px-1.5",
    trending: "mx-[-18px] px-1.5",
};

const VARIANT_CARD_PADDING: Record<SliderVariant, string> = {
    default: "px-2",
    boxed: "px-0 max-[767px]:px-px",
    "boxed-icon": "px-0 max-[767px]:px-px",
    "color-section": "px-2",
    "modal-dots": "px-2",
    "arrow-top": "px-2",
    trending: "px-2",
};

const CustomSlider = ({
    children,
    itemsToShow = 4,
    showArrows = true,
    gap = 16,
    responsive,
    showDots = true,
    className = "",
    activeIndex,
    onSlideChange,
    infinite = false,
    autoSlide = false,
    autoSlideInterval = 3000,
    prevBtnTrackingClass = '',
    nextBtnTrackingClass = '',
    variant = "default",
}: SliderProps) => {
    // Memoize children to prevent "children should not have changed" console error
    const childrenArray = useMemo(() => React.Children.toArray(children), [children]);
    const totalItems = childrenArray.length;

    const [index, setIndex] = useState(() =>
        typeof activeIndex === "number" ? Math.max(0, activeIndex) : 0,
    );
    const [visibleItems, setVisibleItems] = useState(itemsToShow);
    const [isMobile, setIsMobile] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    const startX = useRef(0);
    const isDragging = useRef(false);
    /** Avoid notifying parent on mount / programmatic index sync (breaks controlled modals). */
    const skipIndexNotifyRef = useRef(true);

    // Stability for callback
    const onSlideChangeRef = useRef(onSlideChange);
    useEffect(() => {
        onSlideChangeRef.current = onSlideChange;
    }, [onSlideChange]);

    const maxIndex = Math.max(totalItems - visibleItems, 0);

    const iconClassName = `${ICON_SIZE_OVERRIDE[variant] ?? ""} ${NATURAL_ICON_VARIANTS.includes(variant) ? "" : "invert"}`.trim();

    /* ---------------- Responsive Logic ---------------- */
    // useLayoutEffect (not useEffect) so a mismatch between the server's
    // itemsToShow guess and the real client viewport width is corrected
    // before the browser paints, instead of visibly reflowing the slide
    // widths a frame after first paint (a real, measured CLS contributor).
    //
    // matchMedia instead of window.innerWidth — reading innerWidth here
    // would force a layout flush; matchMedia doesn't need one.
    useLayoutEffect(() => {
        setIsMounted(true);
        const mobileMq = window.matchMedia("(max-width: 639px)");
        const tabletMq = window.matchMedia("(max-width: 1023px)");

        const handleResize = () => {
            const mobile = mobileMq.matches;
            setIsMobile(mobile);

            if (responsive) {
                if (mobile && responsive.mobile) setVisibleItems(responsive.mobile);
                else if (tabletMq.matches && responsive.tablet) setVisibleItems(responsive.tablet);
                else if (responsive.desktop) setVisibleItems(responsive.desktop);
            }
        };
        handleResize();
        mobileMq.addEventListener("change", handleResize);
        tabletMq.addEventListener("change", handleResize);
        return () => {
            mobileMq.removeEventListener("change", handleResize);
            tabletMq.removeEventListener("change", handleResize);
        };
    }, [responsive]);

    useEffect(() => {
        if (index > maxIndex) setIndex(maxIndex);
    }, [visibleItems, maxIndex, index]);

    /* ---------------- Navigation ---------------- */
    const next = useCallback(() =>
        setIndex((i) => (infinite ? (i >= maxIndex ? 0 : i + 1) : Math.min(i + 1, maxIndex))), [infinite, maxIndex]);

    const prev = useCallback(() =>
        setIndex((i) => (infinite ? (i <= 0 ? maxIndex : i - 1) : Math.max(i - 1, 0))), [infinite, maxIndex]);

    useEffect(() => {
        if (typeof activeIndex !== "number") return;
        const nextIndex = Math.min(Math.max(activeIndex, 0), maxIndex);
        setIndex((current) => {
            if (current === nextIndex) return current;
            skipIndexNotifyRef.current = true;
            return nextIndex;
        });
    }, [activeIndex, maxIndex]);

    useEffect(() => {
        if (skipIndexNotifyRef.current) {
            skipIndexNotifyRef.current = false;
            return;
        }
        onSlideChangeRef.current?.(index);
    }, [index]);

    /* ---------------- Auto Slide ---------------- */
    const [isPaused, setIsPaused] = useState(false);
    useEffect(() => {
        if (!autoSlide || totalItems <= visibleItems || isPaused) return;
        const interval = setInterval(next, autoSlideInterval);
        return () => clearInterval(interval);
    }, [autoSlide, autoSlideInterval, next, totalItems, visibleItems, isPaused]);

    /* ---------------- Swipe ---------------- */
    const onTouchStart = (e: React.TouchEvent) => { startX.current = e.touches[0].clientX; isDragging.current = true; };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (!isDragging.current) return;
        const diff = startX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > SWIPE_THRESHOLD) diff > 0 ? next() : prev();
        isDragging.current = false;
    };

    /* ---------------- Dynamic Dots Logic ---------------- */
    const renderDots = () => {
        // Requirement: No dots on mobile or if slider doesn't need to scroll
        if (!isMounted || isMobile || !showDots || maxIndex <= 0) return null;

        /**
         * logic for 4 at start, max 7 in center, 4 at end:
         * We take a window of 3 dots before and 3 dots after the current index.
         * index 0 -> [0, 1, 2, 3] (4 dots)
         * index 3 -> [0, 1, 2, 3, 4, 5, 6] (7 dots)
         * index max -> [max-3, max-2, max-1, max] (4 dots)
         */
        const start = Math.max(0, index - 3);
        const end = Math.min(maxIndex, index + 3);
        const visibleIndices = Array.from({ length: end - start + 1 }, (_, i) => start + i);

        return (
            <div className="flex items-center justify-center gap-[6px] absolute bottom-[-32px] left-1/2 -translate-x-1/2 z-[9] min-h-[12px]">
                {visibleIndices.map((dotIdx) => {
                    const isActive = dotIdx === index;
                    const distance = Math.abs(dotIdx - index);

                    // Dynamic sizing based on distance from active
                    let sizeStyles = "w-[4px] h-[4px]";
                    if (isActive) sizeStyles = "w-[24px] h-[8px]"; // Active Teal Pill
                    else if (distance === 1) sizeStyles = "w-[8px] h-[8px]"; // Medium
                    else if (distance === 2) sizeStyles = "w-[6px] h-[6px]"; // Small

                    return (
                        <button
                            key={`dot-${dotIdx}`}
                            type="button"
                            onClick={() => setIndex(dotIdx)}
                            aria-label={`Go to slide ${dotIdx + 1}`}
                            aria-current={isActive ? "true" : undefined}
                            className={`rounded-full border-0 p-0 transition-all duration-300 cursor-pointer ease-in-out ${
                                isActive ? "bg-[#008C8B]" : "bg-[#D9D9D9]"
                            } ${sizeStyles}`}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div
            className={`relative w-full touch-pan-y ${className}`}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* PREV ARROW */}
            {showArrows && (
                <button
                    role="button"
                    className={`${ARROW_BASE} ${VARIANT_ARROW[variant]} ${VARIANT_LEFT[variant]} ${prevBtnTrackingClass}`}
                    onClick={prev}
                    disabled={!infinite && index === 0}
                >
                    <Image
                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`}
                        alt="Previous"
                        width={20}
                        height={20}
                        title="Previous"
                        className={`rotate-180 ${iconClassName}`}
                    />
                </button>
            )}

            <div className={`overflow-hidden ${VARIANT_TRACK_SPACING[variant]}`}>
                <div
                    className="flex items-start transition-transform duration-400 ease-in-out"
                    style={{ transform: `translateX(-${index * (100 / visibleItems)}%)` }}
                >
                    {childrenArray.map((item, i) => (
                        <div key={`slide-${i}`} className="shrink-0 box-border" style={{ width: `${100 / visibleItems}%` }}>
                            <div className={VARIANT_CARD_PADDING[variant]}>{item}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* NEXT ARROW */}
            {showArrows && (
                <button
                    role="button"
                    className={`${ARROW_BASE} ${VARIANT_ARROW[variant]} ${VARIANT_RIGHT[variant]} ${nextBtnTrackingClass}`}
                    onClick={next}
                    disabled={!infinite && index >= maxIndex}
                >
                    <Image
                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`}
                        alt="Next"
                        width={20}
                        height={20}
                        title="Next"
                        className={iconClassName}
                    />
                </button>
            )}

            {/* DOTS */}
            {renderDots()}
        </div>
    );
};

export default CustomSlider;
